import { useRef, useState } from 'react';
import { Field } from '@/isomorphic/components/forms/components/field';
import { Button } from '@/isomorphic/components/typography/button/button';
import { validators } from '@/hooks/use-validation';
import { useLocalization } from '@/hooks/use-localization';
import { Loading } from '@/isomorphic/components/loading/loading';
import { GoogleRecaptchaInfo } from '@/isomorphic/components/google-recaptcha-info/google-recaptcha-info';
import { CompanyCreditCheckLabels, ContentAlign } from './company-credit-check';
import { CompanyCreditCheckFields } from './use-company-credit-check';

type ContactFieldErrors = {
    email?: string[];
    telephone?: string[];
    name?: string[];
    _form?: string[];
};

const TEXT_ALIGN: Record<ContentAlign, string> = {
    left: 'r-text-left',
    center: 'r-text-center',
};

const BUTTON_ALIGN: Record<ContentAlign, string> = {
    left: '!r-mr-auto',
    center: '!r-mx-auto',
};

type StepTwoProps = {
    labels: CompanyCreditCheckLabels;
    align?: ContentAlign;
    fields: CompanyCreditCheckFields;
    updateField: (updates: Partial<CompanyCreditCheckFields>) => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    contactError: string | null;
    contactFieldErrors: ContactFieldErrors;
    variant?: 'ok' | 'nok';
    captchaError?: boolean;
    enableRecaptcha?: boolean;
};

export const StepTwo = ({
    labels,
    align = 'left',
    fields,
    updateField,
    onSubmit,
    isSubmitting,
    contactError,
    contactFieldErrors,
    variant = 'ok',
    captchaError,
    enableRecaptcha = true,
}: StepTwoProps) => {
    const { getLanguage } = useLocalization();
    const textAlign = TEXT_ALIGN[align];
    const buttonAlign = BUTTON_ALIGN[align];
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const isNameValid = fields.name.trim().length > 0;
    const isEmailValid = validators.email(fields.email);
    const isPhoneValid = validators.phone(fields.telephone, getLanguage());

    const scrollToAndFocusFirstError = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const firstError = containerRef.current.querySelector<HTMLElement>('[aria-invalid="true"]');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    firstError.focus();
                }
            }
        }, 100);
    };

    const handleSubmit = () => {
        setIsFormSubmitted(true);

        if (!isNameValid || !isEmailValid || !isPhoneValid) {
            scrollToAndFocusFirstError();
            return;
        }

        onSubmit();
    };

    const getFieldError = (fieldName: keyof ContactFieldErrors): string | undefined => {
        const errors = contactFieldErrors[fieldName];
        if (errors && errors.length > 0) {
            return errors[0];
        }
        return undefined;
    };

    if (isSubmitting) {
        return (
            <div className="r-my-auto r-flex r-flex-col r-items-center r-justify-center r-gap-4 r-py-16">
                <Loading />
                <p className="r-text-base r-text-gray-500">{labels.sendingText}</p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="r-flex r-flex-col r-gap-6">
            {variant === 'ok' && (
                <div className={`r-flex r-flex-col r-space-y-2 ${textAlign}`}>
                    <h3 className="r-mb-2 r-text-2xl r-font-medium r-text-gray-900">{labels.successTitle}</h3>
                    <div className="r-space-y-2 r-text-base r-text-gray-700" dangerouslySetInnerHTML={{ __html: labels.successDescription }} />
                </div>
            )}

            {variant === 'nok' && (
                <div className={`r-rounded-2xl r-bg-amber-50 r-p-6 ${textAlign}`}>
                    <h3 className="r-mb-2 r-text-2xl r-font-medium r-text-gray-900">{labels.nokTitle}</h3>
                    <p className="r-text-base r-text-gray-700">{labels.nokDescription}</p>
                </div>
            )}

            <div className="r-flex r-flex-col r-gap-4">
                <Field
                    fieldName="name"
                    label={labels.nameLabel}
                    value={fields.name}
                    onChange={value => updateField({ name: value })}
                    validation={value => value.trim().length > 0}
                    errorMessage={getFieldError('name') || labels.nameErrorText}
                    required
                    showRequiredError={isFormSubmitted && !isNameValid}
                />
                <Field
                    fieldName="email"
                    label={labels.emailLabel}
                    value={fields.email}
                    onChange={value => updateField({ email: value })}
                    validation={value => validators.email(value)}
                    errorMessage={getFieldError('email') || labels.emailErrorText}
                    required
                    showRequiredError={isFormSubmitted && !isEmailValid}
                    type="email"
                />
                <Field
                    fieldName="telephone"
                    label={labels.phoneLabel}
                    value={fields.telephone}
                    onChange={value => updateField({ telephone: value })}
                    validation={value => validators.phone(value, getLanguage())}
                    errorMessage={getFieldError('telephone') || labels.phoneErrorText}
                    required
                    showRequiredError={isFormSubmitted && !isPhoneValid}
                    type="tel"
                />
            </div>

            {contactError && (
                <div role="alert" className="r-rounded-xl r-bg-red-100 r-p-4 r-text-sm r-text-red-700">
                    {labels.errorDescription}
                </div>
            )}

            {contactFieldErrors._form && contactFieldErrors._form.length > 0 && (
                <div role="alert" className="r-rounded-xl r-bg-red-100 r-p-4 r-text-sm r-text-red-700">
                    {contactFieldErrors._form[0]}
                </div>
            )}

            {captchaError && (
                <div role="alert" className="r-rounded-xl r-bg-red-100 r-p-4 r-text-sm r-text-red-700">
                    {labels.captchaErrorText}
                </div>
            )}

            <Button type="primary" className={`${buttonAlign} r-w-fit`} onClick={handleSubmit}>
                {variant === 'nok' ? labels.nokContactButtonText : labels.contactButtonText}
            </Button>

            {enableRecaptcha && (
                <div className={textAlign}>
                    <GoogleRecaptchaInfo />
                </div>
            )}
        </div>
    );
};
